/**
 * Caret → board-id mapping for the camera operator.
 *
 * The editor emits the caret's line on every cursor move (a collapsed
 * `editorSelection`, `BoardPane` fans it out). This resolves that line to the
 * chain of module ids the caret sits inside — innermost first — so the focus
 * director can answer "is the caret still inside the node we framed?" (hold the
 * peek) and the scope dial can anchor its rings on the caret's node.
 *
 * Pure and model-aware: `enclosingDeclarations` (a text scan) gives the name
 * chain, then each name resolves to a board key against the compiled model,
 * preferring a declaration in the caret's own file so a same-named module in
 * another package/space can't hijack the anchor. Kept out of `sourceMap.ts`
 * (which is deliberately model-free) and out of `BoardPane` (a component file)
 * so it stays unit-testable.
 */
import {
  flattenModulesWithParent,
  moduleKey,
  type ArchitectureModel,
} from "@archlang/scene/arch-model";
import { enclosingDeclarations } from "./sourceMap.js";

/**
 * Resolve declared/enclosing names to board keys, preferring a module declared
 * in `path`, then any name match. Order-preserving + deduped. The shared
 * resolution used by both the caret chain and the editor-selection framing.
 */
export function resolveNamesToIds(
  names: readonly string[],
  model: ArchitectureModel,
  path: string | undefined,
): string[] {
  const flat = flattenModulesWithParent(model.modules);
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const name of names) {
    const matches = flat.filter((m) => m.name === name);
    // Prefer a module declared in the caret's file; else any name match.
    const pick =
      (path ? matches.find((m) => m.sourceFile === path) : undefined) ?? matches[0];
    if (!pick) continue;
    const key = moduleKey(pick);
    if (!seen.has(key)) {
      seen.add(key);
      ids.push(key);
    }
  }
  return ids;
}

/**
 * True when an editor selection is a "double-click to copy a word" that should
 * NOT frame the board: it came from the pointer as a single bare identifier
 * (`wordLike`), and it names only a *referenced* module — nothing declared in
 * the range. A drag-select (not word-like) or a double-click on a declaration
 * name (declared non-empty) is a deliberate "frame this" and returns false.
 */
export function isCopyWordSelection(
  wordLike: boolean,
  declared: readonly string[],
  referenced: readonly string[],
): boolean {
  return wordLike && declared.length === 0 && referenced.length > 0;
}

/**
 * The chain of board ids the caret (1-based `line` in `text`) sits inside,
 * innermost first. Empty when the caret is outside any declaration, or when
 * the text/model isn't available yet.
 */
export function caretChainIds(
  text: string | undefined,
  line: number,
  model: ArchitectureModel | null,
  path: string | undefined,
): string[] {
  if (!text || !model) return [];
  const names = enclosingDeclarations(text, line); // innermost first
  if (names.length === 0) return [];
  return resolveNamesToIds(names, model, path);
}
